import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectBrandById,
  selectAllFlavors, 
  selectAllAttributes,
  selectGlobalDataLoading,
  selectGlobalDataError,
  selectGlobalDataInitialized
} from "@/store/slices/globalDataSlice";
import { addDrink, selectEditorOperationLoading, selectEditorOperationError } from "@/store/slices/editorSlice";
import type { Flavor, Attribute, DrinkFlavor, DrinkAttribute } from "@/types/firestore";
import { Checkbox } from "@/components/ui/checkbox";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AddDrinkModalProps {
  brandId: string;
  isOpen: boolean;
  onClose: () => void;
  onDrinkAdded: (drinkId: string) => void;
}

export function AddDrinkModal({ brandId, isOpen, onClose, onDrinkAdded }: AddDrinkModalProps) {
  const dispatch = useAppDispatch();
  const allFlavors = useAppSelector(selectAllFlavors);
  const allAttributes = useAppSelector(selectAllAttributes);
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);
  const globalDataError = useAppSelector(selectGlobalDataError);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);
  
  const isSubmitting = useAppSelector(selectEditorOperationLoading) === 'pending';
  const submissionError = useAppSelector(selectEditorOperationError);

  const brand = useAppSelector((state) => selectBrandById(state, brandId));

  const [drinkName, setDrinkName] = useState("");
  const [selectedFlavors, setSelectedFlavors] = useState<string[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);

  const brandNameDisplay = useMemo(() => {
    if (globalDataLoading === 'pending' && !globalDataInitialized) return "Loading brand...";
    return brand ? brand.name : "Unknown Brand";
  }, [brandId, brand, globalDataLoading, globalDataInitialized]);
  
  const isLoading = globalDataLoading === 'pending' && !globalDataInitialized;

  const handleSubmit = async () => {
    if (!brandId || isSubmitting || isLoading) return;

    try {
      const flavorObjects: DrinkFlavor[] = selectedFlavors.map(id => {
        const flavor = allFlavors.find(f => f.id === id);
        return { 
          id, 
          name: flavor?.name || 'Unknown Flavor',
          groupId: flavor?.groupId ?? 0
        };
      });
      
      const attributeObjects: DrinkAttribute[] = selectedAttributes.map(id => {
        const attribute = allAttributes.find(a => a.id === id);
        return { 
          id, 
          name: attribute?.name || 'Unknown Attribute',
          groupId: attribute?.groupId ?? 0
        };
      });

      const resultAction = await dispatch(addDrink({
        name: drinkName.trim(),
        brandId: brandId,
        brandName: brandNameDisplay,
        brandSize: brand?.brandSize || 'unknown',
        flavors: flavorObjects,
        attributes: attributeObjects
      }));

      if (addDrink.fulfilled.match(resultAction) && typeof resultAction.payload === 'string') {
        onDrinkAdded(resultAction.payload);
        handleClose();
      } else {
        console.error("Error creating drink:", resultAction.payload);
      }
    } catch (error) {
      console.error("Error dispatching addDrink:", error);
    }
  };

  const handleClose = () => {
    setDrinkName("");
    setSelectedFlavors([]);
    setSelectedAttributes([]);
    onClose();
  };

  const toggleFlavor = (flavorId: string) => {
    setSelectedFlavors(prev => 
      prev.includes(flavorId)
        ? prev.filter(id => id !== flavorId)
        : [...prev, flavorId]
    );
  };

  const toggleAttribute = (attributeId: string) => {
    setSelectedAttributes(prev => 
      prev.includes(attributeId)
        ? prev.filter(id => id !== attributeId)
        : [...prev, attributeId]
    );
  };

  const renderLoadingError = (loading: boolean, error: string | null, type: string) => {
     if (loading && !globalDataInitialized) return <p className="text-sm text-muted-foreground text-center py-2">Loading {type}...</p>;
     if (error) return <p className="text-sm text-red-500 text-center py-2">Error loading {type}: {error}</p>;
     return null;
  };

  const renderSubmitError = () => {
    if (!submissionError) return null;
    return <p className="text-red-500 text-sm mt-2 text-right">Error: {submissionError}</p>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Drink</DialogTitle>
          <DialogDescription>
            Add a new drink for {brandNameDisplay}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="drinkName">Drink Name</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={16} className="text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-2xs text-center">
                      Leave empty for the original/classic version. For example, 7UP's original flavor has no name, while variants like "Cherry" or "Zero Sugar" do.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id="drinkName"
              value={drinkName}
              onChange={(e) => setDrinkName(e.target.value)}
              placeholder="Leave empty for original flavor"
              disabled={isSubmitting || isLoading}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Flavors</Label>
              <div className="border rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                 {renderLoadingError(isLoading, globalDataError, 'flavors')}
                 {!isLoading && !globalDataError && allFlavors.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No flavors found.</p>}
                 {!isLoading && !globalDataError && allFlavors.map((flavor: Flavor) => (
                   <div key={flavor.id} className="flex items-center space-x-2 ml-2">
                     <Checkbox
                       id={`flavor-${flavor.id}`}
                       checked={selectedFlavors.includes(flavor.id!)}
                       onCheckedChange={() => toggleFlavor(flavor.id!)}
                       disabled={isSubmitting || isLoading}
                     />
                     <label htmlFor={`flavor-${flavor.id}`} className="text-sm">
                       {flavor.name}
                     </label>
                   </div>
                 ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Attributes</Label>
              <div className="border rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                {renderLoadingError(isLoading, globalDataError, 'attributes')}
                {!isLoading && !globalDataError && allAttributes.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No attributes found.</p>}
                {!isLoading && !globalDataError && allAttributes.map((attribute: Attribute) => (
                  <div key={attribute.id} className="flex items-center space-x-2 ml-2">
                    <Checkbox
                      id={`attribute-${attribute.id}`}
                      checked={selectedAttributes.includes(attribute.id!)}
                      onCheckedChange={() => toggleAttribute(attribute.id!)}
                      disabled={isSubmitting || isLoading}
                    />
                    <label htmlFor={`attribute-${attribute.id}`} className="text-sm">
                      {attribute.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {renderSubmitError()}
        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting || isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!brandId || isSubmitting || isLoading || !!globalDataError}
          >
            {isSubmitting ? "Adding..." : "Add Drink"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 